"use client";

import { useState, useEffect, useCallback } from "react";

const STORAGE_KEY = "recentlyViewedProducts";
const MAX_ITEMS = 30;

interface RecentlyViewedItem {
  id: string;
  viewedAt: number;
}

/**
 * 최근 본 상품을 localStorage에 저장하고 관리하는 훅
 * 최대 10개까지 저장, 오래된 것부터 삭제
 */
export function useRecentlyViewed() {
  const [recentlyViewedIds, setRecentlyViewedIds] = useState<string[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);

  // 초기 로드
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const items: RecentlyViewedItem[] = JSON.parse(stored);
        // viewedAt 기준 내림차순 정렬 (최신순)
        const sortedIds = items
          .sort((a, b) => b.viewedAt - a.viewedAt)
          .map((item) => item.id);
        setRecentlyViewedIds(sortedIds);
      }
    } catch (error) {
      console.error("Failed to load recently viewed products:", error);
    }
    setIsLoaded(true);
  }, []);

  // 상품 추가
  const addRecentlyViewed = useCallback((productId: string) => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      let items: RecentlyViewedItem[] = stored ? JSON.parse(stored) : [];

      // 이미 있으면 제거 (최신으로 업데이트하기 위해)
      items = items.filter((item) => item.id !== productId);

      // 새 항목 맨 앞에 추가
      items.unshift({
        id: productId,
        viewedAt: Date.now(),
      });

      // 최대 개수 초과 시 오래된 것 삭제
      if (items.length > MAX_ITEMS) {
        items = items.slice(0, MAX_ITEMS);
      }

      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
      setRecentlyViewedIds(items.map((item) => item.id));
    } catch (error) {
      console.error("Failed to add recently viewed product:", error);
    }
  }, []);

  // 개별 삭제
  const removeRecentlyViewed = useCallback((productId: string) => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return;
      const items: RecentlyViewedItem[] = JSON.parse(stored);
      const filtered = items.filter((item) => item.id !== productId);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
      setRecentlyViewedIds(filtered.map((item) => item.id));
    } catch (error) {
      console.error("Failed to remove recently viewed product:", error);
    }
  }, []);

  // 전체 삭제
  const clearRecentlyViewed = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
      setRecentlyViewedIds([]);
    } catch (error) {
      console.error("Failed to clear recently viewed products:", error);
    }
  }, []);

  return {
    recentlyViewedIds,
    addRecentlyViewed,
    removeRecentlyViewed,
    clearRecentlyViewed,
    isLoaded,
  };
}
